import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search,
  Home,
  Users,
  Filter,
  MapPin,
  ArrowRight,
  X,
  Heart,
  Map,
  LayoutGrid
} from "lucide-react";
import { getPublicLogements } from "@/lib/api";
import LogementsMap from "@/components/LogementsMap";
import { PropertyCard } from "@/components/PropertyCard";

const APARTMENT_IMAGES = [
  "/images/photo-1758548157747.jpg",
  "/images/photo-1760067537740.jpg",
  "/images/photo-1760067537116.jpg",
];

const HERO_BG = "/images/photo-1770736158887.jpg";

export default function VoyageursPage() {
  const [logements, setLogements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVille, setSelectedVille] = useState("");
  const [filters, setFilters] = useState({
    type_logement: "",
    capacite_min: "",
    prix_max: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const navigate = useNavigate();

  useEffect(() => {
    loadLogements();
  }, []);

  const loadLogements = async (params = {}) => {
    setLoading(true);
    try {
      const res = await getPublicLogements(params);
      setLogements(res.data);
    } catch (error) {
      console.error("Error loading logements:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const params = {};
    if (filters.type_logement) params.type_logement = filters.type_logement;
    if (filters.capacite_min) params.capacite_min = parseInt(filters.capacite_min);
    if (filters.prix_max) params.prix_max = parseFloat(filters.prix_max);
    loadLogements(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ type_logement: "", capacite_min: "", prix_max: "" });
    setSelectedVille("");
    loadLogements();
  };

  // Filter by city client-side
  const filteredLogements = selectedVille
    ? logements.filter(l => (l.ville || "").toLowerCase().includes(selectedVille.toLowerCase()))
    : logements;

  const getImage = (logement, index) => {
    if (logement.photos && logement.photos.length > 0) {
      return logement.photos[0];
    }
    return APARTMENT_IMAGES[index % APARTMENT_IMAGES.length];
  };

  return (
    <div className="">
      {/* Hero with background image */}
      <section className="relative py-24 overflow-hidden" data-testid="voyageurs-hero">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C1E14]/75" />
        </div>
        <div className="relative container-hh">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm border border-[#C9922A]/30">Catalogue</span>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-6">
                Ne réservez pas seulement un toit, choisissez votre prochain souvenir
              </h1>
              <p className="text-white/80 text-lg">
                Bienvenue dans la collection Happy House. Que vous fassiez escale pour une nuit d'affaires 
                à Limoges ou que vous posiez vos valises pour trois semaines de ressourcement à Évaux-les-Bains, 
                nous avons dessiné chaque espace pour qu'il devienne le vôtre.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* City filter tabs + Filters */}
      <section className="py-6 bg-[#FFFDF9] border-b border-[#E0D6CA] sticky top-20 z-30" data-testid="filters-bar">
        <div className="container-hh">
          {/* City tabs */}
          <div className="flex items-center gap-3 mb-4" data-testid="city-filter">
            {[
              { value: "", label: "Toutes les villes" },
              { value: "Limoges", label: "Limoges" },
              { value: "Évaux-les-Bains", label: "Évaux-les-Bains" },
            ].map((city) => (
              <button
                key={city.value}
                onClick={() => setSelectedVille(city.value)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedVille === city.value
                    ? "bg-[#C9922A] text-white shadow-[0_2px_12px_rgba(201,146,42,0.3)]"
                    : "bg-[#EDE8E1] text-[#6B5E50] hover:bg-[#D4C9B8] hover:text-[#2D2418]"
                }`}
                data-testid={`city-btn-${city.value || 'all'}`}
              >
                <MapPin className={`w-3.5 h-3.5 inline mr-1.5 -mt-0.5 ${selectedVille === city.value ? "text-white" : "text-[#C9922A]"}`} />
                {city.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 border border-[#E0D6CA] rounded-lg hover:border-[#C9922A] transition-colors"
                data-testid="toggle-filters"
              >
                <Filter className="w-4 h-4" />
                Plus de filtres
              </button>
              
              {(filters.type_logement || filters.capacite_min || filters.prix_max || selectedVille) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-[#8B7D6B] hover:text-[#004333]"
                >
                  <X className="w-4 h-4" />
                  Tout effacer
                </button>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#8B7D6B]">
                {filteredLogements.length} logement{filteredLogements.length > 1 ? "s" : ""} disponible{filteredLogements.length > 1 ? "s" : ""}
              </span>
              <div className="flex bg-[#EDE8E1] rounded-lg p-0.5 ml-2">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === "grid" ? "bg-white shadow-sm text-[#004333]" : "text-[#8B7D6B]"}`}
                  data-testid="view-grid-btn"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === "map" ? "bg-white shadow-sm text-[#004333]" : "text-[#8B7D6B]"}`}
                  data-testid="view-map-btn"
                >
                  <Map className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Filters panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-[#E0D6CA]"
            >
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2418] mb-2">
                    Type de logement
                  </label>
                  <select
                    value={filters.type_logement}
                    onChange={(e) => setFilters({ ...filters, type_logement: e.target.value })}
                    className="input-hh"
                    data-testid="filter-type"
                  >
                    <option value="">Tous</option>
                    <option value="studio">Studio</option>
                    <option value="appartement">Appartement</option>
                    <option value="maison">Maison</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2D2418] mb-2">
                    Capacité minimum
                  </label>
                  <select
                    value={filters.capacite_min}
                    onChange={(e) => setFilters({ ...filters, capacite_min: e.target.value })}
                    className="input-hh"
                    data-testid="filter-capacite"
                  >
                    <option value="">Toutes</option>
                    <option value="1">1 personne</option>
                    <option value="2">2 personnes</option>
                    <option value="4">4 personnes</option>
                    <option value="6">6+ personnes</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#2D2418] mb-2">
                    Prix max / nuit
                  </label>
                  <input
                    type="number"
                    placeholder="Ex: 100"
                    value={filters.prix_max}
                    onChange={(e) => setFilters({ ...filters, prix_max: e.target.value })}
                    className="input-hh"
                    data-testid="filter-prix"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleFilter}
                    className="btn-primary w-full"
                    data-testid="apply-filters"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Logements Grid or Map */}
      <section id="logements" className="section-hh bg-[#F5F0EB]" data-testid="logements-grid">
        <div className="container-hh">
          {viewMode === "map" ? (
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Map */}
              <div className="lg:col-span-3 h-[500px] lg:h-[600px]">
                <LogementsMap
                  logements={filteredLogements}
                  onSelectLogement={(l) => navigate(`/logement/${l.id}`)}
                />
              </div>
              {/* Side list */}
              <div className="lg:col-span-2 max-h-[600px] overflow-y-auto space-y-3 scrollbar-thin pr-1">
                {filteredLogements.map((logement) => (
                  <Link
                    key={logement.id}
                    to={`/logement/${logement.id}`}
                    className="flex gap-3 bg-white rounded-xl border border-[#E0D6CA] p-3 hover:border-[#C9922A]/40 hover:shadow-md transition-all group"
                    data-testid={`map-logement-${logement.id}`}
                  >
                    <img
                      src={logement.images?.[0] || APARTMENT_IMAGES[0]}
                      alt={logement.titre}
                      className="w-24 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-[#004333] truncate group-hover:text-[#C9922A] transition-colors">
                        {logement.titre}
                      </h3>
                      <p className="text-xs text-[#8B7D6B] flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 text-[#C9922A]" />{logement.ville} &bull; {logement.capacite} pers.
                      </p>
                      <span className="font-mono font-bold text-[#C9922A] text-sm mt-1 block">
                        {logement.prix_nuit} EUR<span className="text-[#8B7D6B] font-normal text-xs">/nuit</span>
                      </span>
                    </div>
                  </Link>
                ))}
                {filteredLogements.length === 0 && (
                  <p className="text-center text-[#8B7D6B] py-8">Aucun logement trouvé</p>
                )}
              </div>
            </div>
          ) : loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card-hh animate-pulse">
                  <div className="aspect-[4/3] bg-[#E0D6CA]" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-[#E0D6CA] rounded w-3/4" />
                    <div className="h-4 bg-[#E0D6CA] rounded w-1/2" />
                    <div className="h-8 bg-[#E0D6CA] rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredLogements.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredLogements.map((logement, index) => (
                <motion.div
                  key={logement.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.5 }}
                >
                  <PropertyCard logement={logement} index={index} variant="voyageurs" />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Home className="w-16 h-16 text-[#D4C9B8] mx-auto mb-4" />
              <h3 className="font-display text-xl text-[#004333] mb-2">
                Aucun logement disponible
              </h3>
              <p className="text-[#8B7D6B] mb-6">
                Essayez de modifier vos critères de recherche.
              </p>
              <button onClick={clearFilters} className="btn-secondary">
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ===== LES PLUS HAPPY HOUSE ===== */}
      <section className="py-16 bg-[#FFFDF9] border-b border-[#E0D6CA]">
        <div className="container-hh">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[#6B5E50] text-lg leading-relaxed mb-10 max-w-2xl mx-auto"
            >
              Oubliez les standards froids des hôtels classiques. Ici, vous vivez et respirez la région, avec le confort absolu d'un intérieur pensé par des passionnés.
            </motion.p>

            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              {[
                { emoji: "🛏️", title: "Linge hôtelier", desc: "Linge de maison de qualité hôtelière — draps, serviettes et peignoirs." },
                { emoji: "📖", title: "Guide d'accueil numérique", desc: "Nos meilleures adresses locales : restaurants, visites, bonnes adresses." },
                { emoji: "🔔", title: "Assistance 24h/24", desc: "Notre équipe est disponible à toute heure en cas d'urgence durant votre séjour." },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#F5F0EB] rounded-2xl p-6 border border-[#E0D6CA]"
                >
                  <span className="text-3xl mb-4 block">{item.emoji}</span>
                  <h3 className="font-semibold text-[#004333] mb-2">{item.title}</h3>
                  <p className="text-[#6B5E50] text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <button
              onClick={() => document.getElementById("logements")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004333] text-white font-semibold rounded-full hover:bg-[#005544] transition-colors"
            >
              Explorer nos pépites immobilières <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ===== INVITATION AU VOYAGE — NOS DESTINATIONS ===== */}
      <section className="py-20 bg-[#1A120B]">
        <div className="container-hh">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/30"
            >
              Nos destinations
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display text-3xl md:text-4xl text-white mb-3"
            >
              L'invitation au voyage
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-white/50 italic text-lg"
            >
              "Deux destinations, une seule promesse : vous sentir chez vous, ailleurs."
            </motion.p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Limoges */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden group"
            >
              <img
                src="/images/photo-1669300568006.jpg"
                alt="Limoges"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A120B]/90 via-[#1A120B]/40 to-transparent" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <span className="text-[#C9922A] text-xs font-bold uppercase tracking-widest mb-2">Escale Urbaine</span>
                <h3 className="font-display text-2xl text-white mb-3">Limoges, l'élégance créative</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-4">
                  Plongez au cœur de la capitale des Arts du Feu. Nos appartements, situés dans les quartiers les plus prisés, sont le point de départ idéal pour flâner entre les étals des Halles centrales, admirer les reflets de la Vienne ou découvrir le savoir-faire ancestral des porcelainiers.
                </p>
                <p className="text-[#C9922A] text-xs font-medium italic mb-5">
                  L'esprit Happy House à Limoges : Design contemporain, connectivité totale et douceur de vivre.
                </p>
                <button
                  onClick={() => { setSelectedVille("Limoges"); document.getElementById("logements")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="self-start inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9922A] text-[#1A120B] font-semibold rounded-full text-sm hover:bg-[#E8C874] transition-colors"
                >
                  Vérifier les disponibilités <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Évaux-les-Bains */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden group"
            >
              <img
                src="/images/photo-1659027793165.jpg"
                alt="Évaux-les-Bains"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A120B]/90 via-[#1A120B]/40 to-transparent" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <span className="text-[#C9922A] text-xs font-bold uppercase tracking-widest mb-2">Pause Bien-être</span>
                <h3 className="font-display text-2xl text-white mb-3">Évaux-les-Bains, la parenthèse suspendue</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-4">
                  Laissez le temps s'arrêter. Dans la seule station thermale du Limousin, nous vous offrons des refuges de sérénité. Idéal pour les curistes en quête de confort ou les amateurs de nature sauvage, nos logements sont des écrins de calme à deux pas des thermes et du Casino.
                </p>
                <p className="text-[#C9922A] text-xs font-medium italic mb-5">
                  L'esprit Happy House à Évaux : Sérénité absolue, literie haute densité et horizon verdoyant.
                </p>
                <button
                  onClick={() => { setSelectedVille("Évaux-les-Bains"); document.getElementById("logements")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="self-start inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9922A] text-[#1A120B] font-semibold rounded-full text-sm hover:bg-[#E8C874] transition-colors"
                >
                  Vérifier les disponibilités <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CONTRAT DE CONFIANCE ===== */}
      <section className="py-20 bg-[#F5F0EB]">
        <div className="container-hh">
          <div className="text-center mb-14">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block bg-[#004333]/10 text-[#004333] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#004333]/20"
            >
              Services & Réassurance
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display text-3xl md:text-4xl text-[#004333] mb-3"
            >
              Le contrat de confiance
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-[#6B5E50] italic text-lg mb-3"
            >
              "L'exigence d'un grand hôtel, l'âme d'une maison de famille."
            </motion.p>
            <p className="text-[#8B7D6B] max-w-xl mx-auto text-sm">
              Pour que votre seule préoccupation soit de profiter, nous avons rendu chaque détail invisible mais parfait.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {[
              {
                icon: "✨",
                title: "La Propreté, notre Signature",
                desc: "Un ménage professionnel rigoureux effectué avant chaque arrivée. Votre sécurité et votre confort sanitaire sont nos priorités."
              },
              {
                icon: "🛌",
                title: "Le Sommeil de Plomb",
                desc: "Parce qu'un bon séjour commence par une bonne nuit, nous sélectionnons du linge hôtelier et une literie premium."
              },
              {
                icon: "🔑",
                title: "La Liberté Totale",
                desc: "Arrivée autonome grâce à nos boîtiers sécurisés ou accueil personnalisé — c'est vous qui décidez de votre rythme."
              },
              {
                icon: "📞",
                title: "Assistance 24h/7",
                desc: "Une question ? Un besoin ? Notre équipe locale est à votre écoute pour que rien ne vienne troubler votre parenthèse."
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-7 border border-[#E0D6CA] shadow-sm hover:shadow-md hover:border-[#C9922A]/30 transition-all duration-300"
              >
                <span className="text-4xl mb-5 block">{item.icon}</span>
                <h3 className="font-display text-lg text-[#004333] mb-3">{item.title}</h3>
                <p className="text-[#6B5E50] text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#004333] rounded-2xl p-10 text-center"
          >
            <p className="text-white/70 italic text-lg mb-6">
              "Prêt pour une expérience Happy House ? Vos clés vous attendent déjà."
            </p>
            <button
              onClick={() => document.getElementById("logements")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] font-bold rounded-full shadow-[0_4px_20px_rgba(201,146,42,0.4)] hover:shadow-[0_8px_32px_rgba(201,146,42,0.5)] hover:-translate-y-0.5 transition-all duration-300 text-sm uppercase tracking-wider"
            >
              Vérifier les disponibilités <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>


      <section className="section-hh bg-[#2C1E14]" data-testid="guide-limoges">
        <div className="container-hh">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <span className="inline-block bg-[#C9922A]/20 text-[#C9922A] px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-[#C9922A]/30">Découvrir</span>
              <h2 className="font-display text-3xl md:text-4xl mb-6">
                Escal'urbaine : Limoges, l'élégance créative
              </h2>
              <p className="text-white/70 mb-8">
                Plongez au coeur de la capitale des Arts du Feu. Nos appartements, situés dans les quartiers les plus prisés, 
                sont le point de départ idéal pour flâner entre les étals des Halles centrales, admirer les reflets de la 
                Vienne ou découvrir le savoir-faire ancestral des porcelainiers. Design contemporain, connectivité totale 
                et douceur de vivre.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9922A] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Musée national Adrien Dubouché</h4>
                    <p className="text-white/60 text-sm">La plus grande collection de porcelaine au monde.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9922A] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Cathédrale Saint-Étienne</h4>
                    <p className="text-white/60 text-sm">Chef-d'oeuvre de l'art gothique limousin.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9922A] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Quartier de la Boucherie</h4>
                    <p className="text-white/60 text-sm">Ruelles médiévales et maisons à colombages.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <img
                src="/images/photo-1669300568006.jpg"
                alt="Limoges"
                className="w-full aspect-[4/3] object-cover rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
